import Fuse                   from 'fuse.js'
import { FuseResult }         from 'fuse.js'
import React                  from 'react';
import _uniqBy                from 'lodash/uniqBy';
import useMenu                from '@/hooks/useMenu';
import { useTranslation }     from 'react-i18next';
import useTranslations        from '@/hooks/useTranslations';

export type SearchItem = {
	searchValues: string[],
	tagName: string,
	to: string,
}

type SearchList = SearchItem[]

const useSearch = () => {
	const { i18n } = useTranslation();
	const [searchInputValue, setSearchInputValue] = React.useState('');
	const [searchResults, setSearchResults] = React.useState<FuseResult<SearchItem>[]>([]);
	const [searchList, setSearchList] = React.useState<SearchList>([]);
	const { getNavigationTranslation, getTagTranslation } = useTranslations();
	const { MENU_ITEMS, PAGE_KEYS } = useMenu();

	const getTagNames = (pageKeys: string[]) => {
		
		const tagNames: string[] = [];
		
		pageKeys.forEach(pageKey => {
			switch (pageKey) {
				case 'agenda':
					getTagTranslation(
						pageKey,
						[
							'events',
							'agenda',
							'favorites',
							'artists',
						]
					).forEach(tagName => tagNames.push(tagName));
					break;
				case 'candidacies':
					getTagTranslation(
						pageKey, 
						[
							'candidacies',
							'candidaciesBis',
							'recruitment',
							'HR',
							'seasonalWorkers',
						]
					).forEach(tagName => tagNames.push(tagName));
					break;
				case 'contacts':
					getTagTranslation(
						pageKey, 
						[
							'children',
							'companion',
							'contacts',
							'family',
							'friends',
						]
					).forEach(tagName => tagNames.push(tagName));
					break;
				case 'daisuki':
					getTagTranslation(
						pageKey, 
						[
							'artists',
							'daisuki',
							'favorites',
							'like',
							'likeBis',
						]
					).forEach(tagName => tagNames.push(tagName));
					break;
				case 'newsletters':
					getTagTranslation(
						pageKey, 
						[
							'newsletter',
							'subscription',
							'news',
						]
					).forEach(tagName => tagNames.push(tagName));
					break;
				case 'personalInformation':
					getTagTranslation(
						pageKey, 
						[
							'avatar',
							'gdpr',
							'gender',
							'firstName',
							'lastName',
							'personalData',
							'personalInformation',
							'photo',
							'rightToDelete',
						]
					).forEach(tagName => tagNames.push(tagName));
					break;
				case 'settings':
					getTagTranslation(
						pageKey, [
							'language',
							'emails',
							'preferences',
							'preferencesBis',
						]
					).forEach(tagName => tagNames.push(tagName));
					break;
				case 'security':
					getTagTranslation(
						pageKey, 
						[
							'password',
							'address',
							'changeAddress',
							'changeEmail',
							'changePassword',
							'passwordBis',
							'security',
						]
					).forEach(tagName => tagNames.push(tagName));
					break;
				case 'tickets':
					getTagTranslation(
						pageKey, 
						[
							'ticket',
							'ticketBis',
							'place',
							'eTicket',
							'eTicketBis',
							'entries',
						]
					).forEach(tagName => tagNames.push(tagName));
					break;
				case 'transfers':
					getTagTranslation(
						pageKey,
						[
							'transfer',
							'ticketTransfer',
							'receiveTicket',
						]
					).forEach(tagName => tagNames.push(tagName));
					break;
				case 'stands':
					getTagTranslation(
						pageKey,
						[
							'stands',
							'booths',
							'exhibitors',
						]
					).forEach(tagName => tagNames.push(tagName));
					break;
			}
		})
		return tagNames;
	};

	const getSearchList = () => {

		const searchList: SearchList = [];

		MENU_ITEMS.forEach(menuItem => {
			PAGE_KEYS.forEach(pageKey => {
				if (menuItem.label === getNavigationTranslation(pageKey.label, 'label')) {
					const tagNames = getTagNames([pageKey.label]);
					tagNames.forEach(tagName => {
						searchList.push({ 
							searchValues: [ tagName.normalize('NFD').replace(/[\u0300-\u036F]/g, ''), tagName ], 
							tagName: tagName,
							to: menuItem.to 
						})
					})
				}
			})
		})

		return searchList;
	}

	React.useEffect(() => {
        
		if (searchList.length) {
			const searchOptions = {
				includeScore: true,
				threshold: 0.1,
				keys: ['searchValues']
			}
			
			const fuse = new Fuse(searchList, searchOptions);
	
			const newSearchResults = fuse.search(searchInputValue);

			const uniqueSearchResultsByTagName =  _uniqBy(newSearchResults, result => result.item.tagName);
			
			setSearchResults(uniqueSearchResultsByTagName);
		}

    }, [searchInputValue]); 

	React.useEffect(() => {
		const searchList = getSearchList();

		setSearchList(searchList);
	}, [i18n.language]);

	return {
		getSearchList,
		getTagNames,
		searchInputValue,
		setSearchInputValue,
		searchResults,
	}
}

export default useSearch;